import XLSX from 'xlsx-js-style';
import { TYPE_LABEL_MAP } from './shared';
const titleStyle = {
    font: { bold: true, sz: 20, color: { rgb: '181D27' } },
    alignment: { horizontal: 'center', vertical: 'center' },
};
const columnHeaderStyle = {
    font: { bold: true, sz: 11, color: { rgb: '374151' } },
    fill: { fgColor: { rgb: 'E5E7EB' } },
    alignment: { horizontal: 'center', vertical: 'center' },
};
/**
 * Export Active Client List to XLSX using the same xlsx-js-style package
 * that Divergence Report module uses.
 * Each client has one main row plus one row per multiple_networks entry.
 *
 * @param rows Active client rows from list-with-icp API
 * @param accountUserMap Optional map of userId -> full name (from designationWiseUsers)
 */
export function exportActiveClientList(rows, accountUserMap) {
    const formatDate = (value) => {
        if (!value)
            return '';
        try {
            const d = new Date(value);
            if (isNaN(d.getTime()))
                return '';
            // DD/MM/YYYY format
            return d.toLocaleDateString('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
            });
        }
        catch {
            return '';
        }
    };
    const mapTypeLabels = (value) => {
        if (!value)
            return '';
        const toLabel = (t) => {
            if (t == null)
                return '';
            const key = String(t).trim().toLowerCase();
            return TYPE_LABEL_MAP[key] ?? key;
        };
        // ✅ Handle both array and string
        let types = [];
        if (Array.isArray(value)) {
            types = value;
        }
        else if (typeof value === 'string') {
            // Handle comma-separated string
            types = value.split(',').map(t => t.trim()).filter(Boolean);
        }
        else {
            types = [value];
        }
        const labels = types
            .map(toLabel)
            .map((s) => s.trim())
            .filter((s) => s.length > 0);
        return labels.join(', ');
    };
    const formatPeople = (value) => {
        if (!value)
            return '';
        const extractName = (v) => {
            if (!v)
                return '';
            if (typeof v === 'string')
                return v;
            if (typeof v === 'object') {
                const full = `${v.first_name ?? ''} ${v.last_name ?? ''}`.trim() ||
                    v.full_name ||
                    v.name ||
                    '';
                return full;
            }
            return String(v);
        };
        if (Array.isArray(value)) {
            const names = value
                .map(extractName)
                .map((n) => n.trim())
                .filter((n) => n.length > 0);
            return names.join(', ');
        }
        return extractName(value);
    };
    const buildInitialsMap = () => {
        const map = {};
        if (!accountUserMap)
            return map;
        const getInitialsFromName = (name) => {
            if (!name)
                return '';
            const parts = name
                .split(' ')
                .map((p) => p.trim())
                .filter(Boolean);
            if (!parts.length)
                return '';
            if (parts.length === 1)
                return parts[0].slice(0, 2).toUpperCase();
            return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
        };
        Object.values(accountUserMap).forEach((full) => {
            const initials = getInitialsFromName(full);
            if (!initials)
                return;
            const key = initials.toUpperCase();
            if (!map[key])
                map[key] = [];
            if (!map[key].includes(full)) {
                map[key].push(full);
            }
        });
        return map;
    };
    const initialsMap = buildInitialsMap();
    const resolveCloser = (value) => {
        if (!value)
            return '';
        const items = Array.isArray(value) ? value : [value];
        const results = [];
        items.forEach((item) => {
            let token = '';
            if (typeof item === 'string') {
                token = item.trim();
            }
            else if (item && typeof item === 'object') {
                token =
                    `${item.first_name ?? ''} ${item.last_name ?? ''}`.trim() ||
                        item.full_name ||
                        item.name ||
                        '';
            }
            if (!token)
                return;
            // If token already looks like a full name (has a space), use as-is
            if (token.includes(' ')) {
                if (!results.includes(token))
                    results.push(token);
                return;
            }
            // Treat as initials and try to map via initialsMap
            const key = token.toUpperCase();
            const mapped = initialsMap[key];
            if (mapped && mapped.length) {
                mapped.forEach((full) => {
                    if (!results.includes(full))
                        results.push(full);
                });
            }
            else {
                // Fallback to the token itself if no mapping found
                if (!results.includes(token))
                    results.push(token);
            }
        });
        return results.join(', ');
    };
    if (!rows || !Array.isArray(rows) || rows.length === 0) {
        alert('No clients available to export');
        return;
    }
    try {
        // Columns aligned with frontend expanded row display
        const header = [
            'Client Name',
            'Category',
            'Type',
            'Start Date',
            'Termination Date',
            'MRR',
            'Commission',
            'Website',
            'Lead AM',
            'Past AM',
            'AM',
            'Social Ass.',
            'Social Lead',
            'OS',
            'Search Lead',
            'Search Ass.',
            'Email Lead',
            'Email Ass.',
            'Design Lead',
            'Company Size in Revenue',
            'Employee Count',
            'B2B/B2C',
            'App/Services/Retail',
            'Primary Region',
            'Organic Traffic (Before)',
            '3% Fee Increase',
            'Access',
            'Affiliate Network',
            'Program Id',
            'Sign-Up Link',
            'Alias',
            'Sample Status',
            'Brand Description',
            'Closer',
            'Payment Method',
        ];
        const mapHandlerIdsToNames = (ids, row) => {
            if (!ids || !accountUserMap)
                return '';
            const arr = Array.isArray(ids) ? ids : [ids];
            const names = arr
                .map((id) => {
                const key = typeof id === 'string'
                    ? id
                    : (id && typeof id === 'object' && (id._id || id.id)) || String(id);
                return accountUserMap[key] || '';
            })
                .map((n) => n.trim())
                .filter((n) => n.length > 0);
            return names.join(', ');
        };
        const data = [];
        rows.forEach((row) => {
            const icp = row?.icp || {};
            const handlers = row.account_handler || null;
            const types = mapTypeLabels(row?.type);
            const website = icp.website ?? row?.website_url ?? '';
            const leadAmDisplay = formatPeople(icp.lead_account_manager) ||
                mapHandlerIdsToNames(handlers?.am_id, row) ||
                formatPeople(row?.account_managers);
            const pastAmDisplay = mapHandlerIdsToNames(handlers?.past_am_id, row);
            const amDisplay = formatPeople(icp.account_manager) ||
                mapHandlerIdsToNames(handlers?.assoc_am_id, row);
            const socialAssDisplay = mapHandlerIdsToNames(handlers?.assoc_paid_social_id, row);
            const socialLeadDisplay = mapHandlerIdsToNames(handlers?.paid_social_id, row);
            const osDisplay = formatPeople(icp.out_reach_specialist) ||
                mapHandlerIdsToNames(handlers?.assoc_affiliate_lead_id || handlers?.affiliate_lead_id, row);
            const searchLeadDisplay = mapHandlerIdsToNames(handlers?.paid_search_id, row);
            const searchAssDisplay = mapHandlerIdsToNames(handlers?.assoc_paid_search_id, row);
            const emailLeadDisplay = mapHandlerIdsToNames(handlers?.email_lead_id, row);
            const emailAssDisplay = mapHandlerIdsToNames(handlers?.assoc_email_lead_id, row);
            const designLeadDisplay = mapHandlerIdsToNames(handlers?.design_lead_id, row);
            const companySize = (icp.company_size_in_revenue ?? icp.company_size_revenue ?? '').toString().trim();
            const employeeCount = (icp.employee_count ?? '').toString().trim();
            const b2b = (icp.b2B_or_b2C ?? icp.b2b_b2c ?? '').toString().trim();
            const appServices = (icp.app_services_retail ?? icp.vertical ?? '').toString().trim();
            const primaryRegion = (icp.primary_region ?? icp.geo ?? '').toString().trim();
            const organicTraffic = (icp.organic_traffic_before_we_start_with_them ?? icp.organic_traffic_before ?? '').toString().trim();
            const threePercentFeeIncrease = (icp.three_percent_fee_increase ?? '').toString().trim();
            const sampleStatus = (icp.sample_status ?? '').toString().trim();
            const brandDesc = (icp.brand_description ?? '').toString().trim();
            const closerDisplay = resolveCloser(icp.closer);
            // MRR / Commission — mirror the frontend grid formatting ($ 2,500.00 / 5 %)
            const mrrDisplay = icp.contract_mrr != null
                ? `$ ${Number(icp.contract_mrr).toLocaleString('en-US', { minimumFractionDigits: 2 })}`
                : '';
            let commissionDisplay = icp.contract_commission_rate != null
                ? String(icp.contract_commission_rate).replace(/%\s*$/, '').replace(/\$\s*/g, '').trim()
                : '';
            if (commissionDisplay.toLowerCase() === 'null')
                commissionDisplay = '';
            commissionDisplay = commissionDisplay !== '' ? `${commissionDisplay} %` : '';
            const clientCols = [
                row?.name ?? '',
                icp.category ?? '',
                types,
                formatDate(row?.start_date),
                formatDate(row?.termination_date),
                mrrDisplay,
                commissionDisplay,
                website,
                leadAmDisplay,
                pastAmDisplay,
                amDisplay,
                socialAssDisplay,
                socialLeadDisplay,
                osDisplay,
                searchLeadDisplay,
                searchAssDisplay,
                emailLeadDisplay,
                emailAssDisplay,
                designLeadDisplay,
                companySize,
                employeeCount,
                b2b,
                appServices,
                primaryRegion,
                organicTraffic,
                threePercentFeeIncrease,
                icp.access ?? '',
            ];
            const clientTailCols = [
                icp.alias ?? '',
                sampleStatus,
                brandDesc,
                closerDisplay,
                icp.payment_method ?? '',
            ];
            // Main row: client info + main network (affiliate_network, program_id, sign_up_link)
            data.push([
                ...clientCols,
                icp.affiliate_network ?? '',
                icp.program_id ?? '',
                icp.sign_up_link ?? '',
                ...clientTailCols,
            ]);
            // Additional rows: one per multiple_networks, blank client columns, network-specific columns filled
            const multipleNetworks = Array.isArray(icp.multiple_networks) ? icp.multiple_networks : [];
            const blankClientCols = Array(clientCols.length).fill('');
            const blankTailCols = Array(clientTailCols.length).fill('');
            multipleNetworks.forEach((m) => {
                const netName = (m?.name ?? m?.affiliate_network ?? '').toString().trim();
                const programId = (m?.program_id ?? '').toString().trim();
                const signUpLink = (m?.sign_up_link ?? '').toString().trim();
                data.push([
                    ...blankClientCols,
                    netName,
                    programId,
                    signUpLink,
                    ...blankTailCols,
                ]);
            });
        });
        const numCols = header.length;
        const titleRow = ['Active Client List', ...Array(numCols - 1).fill('')];
        const spacerRow = Array(numCols).fill('');
        const sheetData = [titleRow, spacerRow, header, ...data];
        const worksheet = XLSX.utils.aoa_to_sheet(sheetData);
        if (worksheet['A1']) {
            worksheet['A1'].s = titleStyle;
        }
        worksheet['!merges'] = [
            { s: { r: 0, c: 0 }, e: { r: 0, c: numCols - 1 } },
        ];
        for (let c = 0; c < numCols; c++) {
            const cellRef = XLSX.utils.encode_cell({ r: 2, c });
            if (worksheet[cellRef]) {
                worksheet[cellRef].s = columnHeaderStyle;
            }
        }
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Active Client List');
        const fileName = 'Active_Client_List.xlsx';
        XLSX.writeFile(workbook, fileName);
    }
    catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to export active client list:', error);
        alert('Failed to export Active Client List. Please try again.');
    }
}
export default exportActiveClientList;
